import Vue from "vue";

import ProductForm from "../ProductForm/ProductForm.vue";
import ProductViewForm from "../ProductViewForm/ProductViewForm.vue";
import ProductTypeForm from "../ProductTypeForm/ProductTypeForm.vue";
import ProductTypeViewForm from "../ProductTypeViewForm/ProductTypeViewForm.vue";
import ProductContainerForm from "../ProductContainerForm/ProductContainerForm.vue";
import ProductContainerViewForm from "../ProductContainerViewForm/ProductContainerViewForm.vue";

export type CatalogEntityKind = "type" | "parent" | "product" | "container";

export interface OpenCatalogEntityOptions {
  parent?: Vue;
  onClose?: () => void;
}

export function getIsCatalog(vm: Vue): boolean {
  return vm.$store.getters["ubic/isCatalog"];
}

export function canEditCatalogEntities(vm: Vue): boolean {
  const getters = vm.$store.getters;
  return !!(getters["ubic/isCatalog"] || getters["ubic/isAdmin"]);
}

const DOCAPI_ID_RE = /^d:([^:]+):(\d+)$/;

export function catalogEntitySchema(kind: CatalogEntityKind): string {
  switch (kind) {
    case "type":
    case "parent":
      return "products_types";
    case "product":
      return "products";
    case "container":
      return "product_containers";
  }
}

export function toCatalogEntityDocId(
  kind: CatalogEntityKind,
  numericId: number
): string {
  return `d:${catalogEntitySchema(kind)}:${numericId}`;
}

export function parseCatalogEntityId(
  id: string | number | null | undefined,
  kind?: CatalogEntityKind
): string | number | null {
  if (id == null || id === "") {
    return null;
  }

  const raw = String(id).trim();

  if (DOCAPI_ID_RE.test(raw)) {
    return raw;
  }

  if (/^\d+$/.test(raw)) {
    const numeric = Number(raw);
    if (numeric > 0 && kind) {
      return toCatalogEntityDocId(kind, numeric);
    }
    if (numeric > 0) {
      return numeric;
    }
  }

  return null;
}

export function buildDocumentPreview(
  id: string | number | null | undefined,
  kind?: CatalogEntityKind
) {
  const parsed = parseCatalogEntityId(id, kind);
  return parsed != null ? { id: parsed } : null;
}

export function resolveCatalogEntityId(
  vm: Vue,
  documentPreview?: { id?: string | number } | null,
  kind?: CatalogEntityKind
): string | number | null {
  const entityKind =
    kind ||
    (vm.$route &&
      vm.$route.meta &&
      (vm.$route.meta.catalogEntity as CatalogEntityKind));

  const fromPreview = documentPreview && documentPreview.id;
  const fromRoute =
    vm.$route && vm.$route.params && vm.$route.params.id;

  return (
    parseCatalogEntityId(fromPreview, entityKind) ||
    parseCatalogEntityId(fromRoute, entityKind)
  );
}

export function catalogEntityRouteName(kind: CatalogEntityKind): string {
  switch (kind) {
    case "type":
      return "product-catalog-type";
    case "parent":
      return "product-catalog-parent";
    case "product":
      return "product-catalog-product";
    case "container":
      return "product-catalog-container";
  }
}

export function catalogEntityKindFromRow(row: any): CatalogEntityKind | null {
  if (!row || !row.id) {
    return null;
  }

  switch (row.type) {
    case "type":
      return "type";
    case "parent":
      return "parent";
    case "product":
      return "product";
    case "container":
      return "container";
    default:
      if (row.record_type === "parent_product") {
        return "parent";
      }
      if (row.record_type === "type") {
        return "type";
      }
      return null;
  }
}

export function openCatalogEntityRoute(vm: Vue, row: any): boolean {
  const kind = catalogEntityKindFromRow(row);
  if (!kind) {
    return false;
  }

  vm.$router.push({
    name: catalogEntityRouteName(kind),
    params: { id: String(row.id) }
  });
  return true;
}

function getEditModalConfig(
  kind: CatalogEntityKind,
  documentPreview: { id: string | number } | null
) {
  switch (kind) {
    case "type":
      return {
        component: ProductTypeForm,
        props: {
          isNew: false,
          documentPreview,
          initParams: {}
        },
        customClass: "document-form short-form"
      };
    case "parent":
      return {
        component: ProductTypeForm,
        props: {
          isNew: false,
          documentPreview,
          initParams: {}
        },
        customClass: "document-form"
      };
    case "product":
      return {
        component: ProductForm,
        props: {
          isNew: false,
          documentPreview,
          initParams: {}
        },
        customClass: "document-form"
      };
    case "container":
      return {
        component: ProductContainerForm,
        props: {
          isNew: false,
          documentPreview,
          initParams: {}
        },
        customClass: "document-form short-form"
      };
  }
}

function getViewModalConfig(
  kind: CatalogEntityKind,
  documentPreview: { id: string | number } | null
) {
  switch (kind) {
    case "type":
    case "parent":
      return {
        component: ProductTypeViewForm,
        props: { documentPreview },
        customClass: "document-form"
      };
    case "product":
      return {
        component: ProductViewForm,
        props: { documentPreview },
        customClass: "document-form"
      };
    case "container":
      return {
        component: ProductContainerViewForm,
        props: { documentPreview },
        customClass: "document-form"
      };
  }
}

export function getCatalogEntityFormConfig(
  kind: CatalogEntityKind,
  id: string | number | null | undefined,
  canEdit: boolean
) {
  const documentPreview = buildDocumentPreview(id, kind);
  const allowEdit = !!canEdit;
  const config = allowEdit
    ? getEditModalConfig(kind, documentPreview)
    : getViewModalConfig(kind, documentPreview);

  console.debug("[openCatalogEntity.getCatalogEntityFormConfig]", {
    kind,
    id,
    canEdit,
    allowEdit
  });

  return {
    documentPreview,
    ...config,
    hasFormClose: allowEdit
  };
}

export function openCatalogEntityModal(
  vm: Vue,
  kind: CatalogEntityKind,
  id: string | number | null | undefined,
  options: OpenCatalogEntityOptions = {}
) {
  const documentPreview = buildDocumentPreview(id, kind);
  if (!documentPreview) {
    return null;
  }

  const canEdit = canEditCatalogEntities(vm);
  const parent = options.parent || vm;
  const onClose = options.onClose;
  const config = canEdit
    ? getEditModalConfig(kind, documentPreview)
    : getViewModalConfig(kind, documentPreview);

  console.debug("[openCatalogEntity.openCatalogEntityModal]", {
    kind,
    id,
    canEdit
  });

  let closed = false;
  const handleClose = () => {
    if (closed) {
      return;
    }
    closed = true;
    if (onClose) {
      onClose();
    }
  };

  const modal = vm.$buefy.modal.open({
    component: config.component,
    props: config.props,
    events: {
      "form-close": handleClose
    },
    hasModalCard: true,
    fullScreen: true,
    customClass: config.customClass,
    parent,
    onCancel: handleClose
  });

  // `$parent.close()` on forms calls Modal.close(), which does not always
  // trigger onCancel — listen to close so URL resets for view and edit.
  if (modal && typeof (modal as any).$once === "function") {
    (modal as any).$once("close", handleClose);
  }

  return modal;
}
